import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, User, Mail, Lock, Moon, Sun, Shield, Eye, EyeOff, Save, Camera, Trash2, AlertTriangle, Sparkles, HelpCircle, LogOut, Activity } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Auth handled via Bearer token in axios interceptor (App.js)

function SettingsPage({ onThemeChange, onLogout }) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Profile edit
  const [editName, setEditName] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  
  // Password
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');
  
  // PIN
  const [showPinSection, setShowPinSection] = useState(false);
  
  // Delete account
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState('');

  // Secret code
  const [secretCode, setSecretCode] = useState('');
  const [secretMsg, setSecretMsg] = useState('');
  const isPrettyActive = document.documentElement.classList.contains('pretty');
  const [activeTheme, setActiveTheme] = useState(() => {
    if (document.documentElement.classList.contains('sunset')) return 'sunset';
    if (document.documentElement.classList.contains('twilight')) return 'twilight';
    if (document.documentElement.classList.contains('pretty')) return 'pretty';
    return 'default';
  });
  
  const [pinDigits, setPinDigits] = useState(['', '', '', '']);
  const [pinMsg, setPinMsg] = useState('');
  const [pinAuthMode, setPinAuthMode] = useState(null); // 'old_pin' | 'email' | null
  const [oldPinDigits, setOldPinDigits] = useState(['', '', '', '']);
  const [emailPassword, setEmailPassword] = useState('');
  const [pinAuthenticated, setPinAuthenticated] = useState(false);
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      setEditName(res.data.name);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      await axios.put(`${API}/settings/profile`, { name: editName });
      setProfileMsg('Saved');
      setTimeout(() => setProfileMsg(''), 2000);
    } catch (err) {
      setProfileMsg('Failed to save');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      setPasswordMsg('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API}/settings/password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      setPasswordMsg('Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setShowPasswordSection(false);
      setTimeout(() => setPasswordMsg(''), 2000);
    } catch (err) {
      setPasswordMsg(err.response?.data?.detail || 'Failed to update password');
    }
  };

  const handlePinDigit = (index, value, target = 'new') => {
    if (value && !/^\d$/.test(value)) return;
    const setter = target === 'old' ? setOldPinDigits : setPinDigits;
    const digits = target === 'old' ? oldPinDigits : pinDigits;
    const newDigits = [...digits];
    newDigits[index] = value;
    setter(newDigits);
    
    if (value && index < 3) {
      const next = document.getElementById(`${target}-pin-${index + 1}`);
      if (next) next.focus();
    }
  };

  const handlePinKeyDown = (index, e, target = 'new') => {
    const digits = target === 'old' ? oldPinDigits : pinDigits;
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      const prev = document.getElementById(`${target}-pin-${index - 1}`);
      if (prev) prev.focus();
    }
  };

  const handleTogglePin = async () => {
    if (settings.pin_enabled) {
      // Need to authenticate before disabling
      setPinAuthMode('old_pin');
      setShowPinSection(true);
      setPinAuthenticated(false);
    } else {
      setShowPinSection(true);
      setPinAuthMode(null);
      setPinAuthenticated(false);
    }
  };

  const handleVerifyOldPin = async () => {
    const pin = oldPinDigits.join('');
    if (pin.length !== 4) { setPinMsg('Enter all 4 digits'); return; }
    try {
      const res = await axios.post(`${API}/settings/verify-pin`, { pin });
      if (res.data.valid) {
        setPinAuthenticated(true);
        setPinMsg('');
        setOldPinDigits(['', '', '', '']);
      } else {
        setPinMsg('Incorrect PIN');
        setOldPinDigits(['', '', '', '']);
      }
    } catch (err) {
      setPinMsg('Verification failed');
    }
  };

  const handleVerifyEmail = async () => {
    if (!emailPassword) { setPinMsg('Enter your password'); return; }
    try {
      await axios.post(`${API}/auth/email/login`, { email: settings.email, password: emailPassword });
      setPinAuthenticated(true);
      setPinMsg('');
      setEmailPassword('');
    } catch (err) {
      setPinMsg('Incorrect password');
    }
  };

  const handleDisablePin = async () => {
    try {
      await axios.put(`${API}/settings/pin`, { action: 'disable' });
      setSettings(s => ({ ...s, pin_enabled: false }));
      setPinMsg('PIN disabled');
      setPinDigits(['', '', '', '']);
      setShowPinSection(false);
      setPinAuthenticated(false);
      setPinAuthMode(null);
      setTimeout(() => setPinMsg(''), 2000);
    } catch (err) {
      setPinMsg('Failed');
    }
  };

  const handleSavePin = async () => {
    const pin = pinDigits.join('');
    if (pin.length !== 4) {
      setPinMsg('Enter all 4 digits');
      return;
    }
    try {
      const action = settings.pin_enabled ? 'change' : 'enable';
      await axios.put(`${API}/settings/pin`, { action, pin });
      setSettings(s => ({ ...s, pin_enabled: true }));
      setPinMsg(action === 'change' ? 'PIN changed' : 'PIN enabled');
      setPinDigits(['', '', '', '']);
      setShowPinSection(false);
      setPinAuthenticated(false);
      setPinAuthMode(null);
      setTimeout(() => setPinMsg(''), 2000);
    } catch (err) {
      setPinMsg(err.response?.data?.detail || 'Failed');
    }
  };

  const handleToggleDarkMode = async () => {
    const newVal = !settings.dark_mode;
    try {
      await axios.put(`${API}/settings/dark-mode`, { enabled: newVal });
      setSettings(s => ({ ...s, dark_mode: newVal }));
      if (onThemeChange) onThemeChange(newVal);
    } catch (err) {
      console.error('Error toggling dark mode:', err);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteMsg('Please type DELETE to confirm');
      return;
    }
    setIsDeletingAccount(true);
    setDeleteMsg('');
    try {
      await axios.delete(`${API}/account`, { data: { confirmation: 'DELETE' } });
      navigate('/');
      window.location.reload();
    } catch (err) {
      setDeleteMsg(err.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleSecretCode = () => {
    const removeAllThemes = () => {
      document.documentElement.classList.remove('pretty', 'sunset', 'twilight');
      localStorage.removeItem('noyo_pretty');
      localStorage.removeItem('noyo_sunset');
      localStorage.removeItem('noyo_twilight');
    };

    if (secretCode.toLowerCase() === 'pretty') {
      const isCurrentlyPretty = document.documentElement.classList.contains('pretty');
      removeAllThemes();
      
      if (isCurrentlyPretty) {
        setSecretMsg('Theme restored');
        setActiveTheme('default');
      } else {
        document.documentElement.classList.add('pretty');
        localStorage.setItem('noyo_pretty', 'true');
        setSecretMsg('Just for u ;)');
        setActiveTheme('pretty');
      }
      setSecretCode('');
      setTimeout(() => setSecretMsg(''), 4000);
    } else if (secretCode.toLowerCase() === '83ormsbyrd') {
      const isCurrentlySunset = document.documentElement.classList.contains('sunset');
      removeAllThemes();
      
      if (isCurrentlySunset) {
        setSecretMsg('Theme restored');
        setActiveTheme('default');
      } else {
        document.documentElement.classList.add('sunset');
        localStorage.setItem('noyo_sunset', 'true');
        setSecretMsg('Golden hour activated');
        setActiveTheme('sunset');
      }
      setSecretCode('');
      setTimeout(() => setSecretMsg(''), 4000);
    } else if (secretCode.toLowerCase() === 'pretty2') {
      const isCurrentlyTwilight = document.documentElement.classList.contains('twilight');
      removeAllThemes();
      
      if (isCurrentlyTwilight) {
        setSecretMsg('Theme restored');
        setActiveTheme('default');
      } else {
        document.documentElement.classList.add('twilight');
        localStorage.setItem('noyo_twilight', 'true');
        setSecretMsg('Twilight dreams');
        setActiveTheme('twilight');
      }
      setSecretCode('');
      setTimeout(() => setSecretMsg(''), 4000);
    } else {
      setSecretMsg('Nuh uh sorry');
      setSecretCode('');
      setTimeout(() => setSecretMsg(''), 2000);
    }
  };

  const switchTheme = (theme) => {
    const removeAllThemes = () => {
      document.documentElement.classList.remove('pretty', 'sunset', 'twilight');
      localStorage.removeItem('noyo_pretty');
      localStorage.removeItem('noyo_sunset');
      localStorage.removeItem('noyo_twilight');
    };
    removeAllThemes();
    if (theme === 'default' || theme === activeTheme) {
      setActiveTheme('default');
      return;
    }
    document.documentElement.classList.add(theme);
    localStorage.setItem(`noyo_${theme}`, 'true');
    if (theme === 'twilight') {
      document.documentElement.classList.add('dark');
      localStorage.setItem('noyo_dark_mode', 'true');
      setSettings(prev => ({ ...prev, dark_mode: true }));
    }
    if (theme === 'sunset') {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('noyo_dark_mode', 'false');
      setSettings(prev => ({ ...prev, dark_mode: false }));
    }
    setActiveTheme(theme);
  };


  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-background pt-20 md:pt-24 pb-12"
    >
      <div className="max-w-xl mx-auto px-4 md:px-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="settings-back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Back</span>
        </button>

        <h1 className="text-2xl md:text-3xl font-serif font-medium text-foreground mb-8" data-testid="settings-title">Settings</h1>

        {/* Profile Section */}
        <div className="bg-card border border-border rounded-2xl p-5 md:p-6 mb-4" data-testid="settings-profile-section">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">Profile</h2>
          
          <div className="flex items-center space-x-4 mb-5">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center overflow-hidden">
              {settings.picture ? (
                <img src={settings.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground flex items-center space-x-1">
                <Mail className="w-3.5 h-3.5" />
                <span>{settings.email}</span>
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Name</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 bg-secondary/50 dark:bg-secondary border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                  data-testid="settings-name-input"
                />
                <button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile || editName === settings.name}
                  className="bg-primary text-white rounded-xl px-4 py-2 text-sm font-medium hover:bg-primary/80 disabled:opacity-50 transition-colors flex items-center space-x-1"
                  data-testid="settings-save-name"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>Save</span>
                </button>
              </div>
              {profileMsg && <p className="text-xs text-primary mt-1">{profileMsg}</p>}
            </div>
          </div>
        </div>

        {/* Password Section */}
        {settings.auth_type === 'email' && (
          <div className="bg-card border border-border rounded-2xl p-5 md:p-6 mb-4" data-testid="settings-password-section">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Password</h2>
              <button
                onClick={() => setShowPasswordSection(!showPasswordSection)}
                className="text-xs text-primary hover:text-primary/80 transition-colors"
                data-testid="settings-change-password-toggle"
              >
                {showPasswordSection ? 'Cancel' : 'Change'}
              </button>
            </div>
            
            {showPasswordSection && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-4 space-y-3">
                <div className="relative">
                  <input
                    type={showCurrentPw ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password"
                    className="w-full bg-input/30 border border-border rounded-xl px-3 py-2 text-sm pr-10 focus:outline-none focus:border-primary"
                    data-testid="settings-current-password"
                  />
                  <button onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showNewPw ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 6 chars)"
                    className="w-full bg-input/30 border border-border rounded-xl px-3 py-2 text-sm pr-10 focus:outline-none focus:border-primary"
                    data-testid="settings-new-password"
                  />
                  <button onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  onClick={handleChangePassword}
                  className="bg-primary text-white rounded-xl px-4 py-2 text-sm font-medium hover:bg-primary/80 transition-colors"
                  data-testid="settings-save-password"
                >
                  Update Password
                </button>
                {passwordMsg && <p className="text-xs text-primary">{passwordMsg}</p>}
              </motion.div>
            )}
          </div>
        )}

        {/* Appearance */}
        <div className="bg-card border border-border rounded-2xl p-5 md:p-6 mb-4" data-testid="settings-appearance-section">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">Appearance</h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {settings.dark_mode ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-accent" />}
              <div>
                <p className="text-sm font-medium text-foreground">Dark Mode</p>
                <p className="text-xs text-muted-foreground">{settings.dark_mode ? 'On' : 'Off'}</p>
              </div>
            </div>
            <button
              onClick={handleToggleDarkMode}
              className={`relative w-12 h-7 rounded-full transition-colors ${settings.dark_mode ? 'bg-primary' : 'bg-secondary'}`}
              data-testid="settings-dark-mode-toggle"
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200 ${settings.dark_mode ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Theme Palette */}
          <div className="mt-5 pt-4 border-t border-border/50">
            <p className="text-xs text-muted-foreground mb-3">Theme</p>
            <div className="flex items-center gap-3">
              {[
                { id: 'default', color: '#8DA399', label: 'Default' },
                { id: 'sunset', color: '#E07B39', label: 'Sunset' },
                { id: 'twilight', color: '#9B6AA6', label: 'Twilight' },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => switchTheme(t.id)}
                  className={`w-8 h-8 rounded-full transition-all duration-200 flex-shrink-0 ${activeTheme === t.id ? 'scale-110' : 'hover:scale-105 opacity-60 hover:opacity-100'}`}
                  style={{ 
                    backgroundColor: t.color,
                    boxShadow: activeTheme === t.id ? `0 0 0 2.5px var(--color-card-solid), 0 0 0 4.5px ${t.color}` : 'none'
                  }}
                  data-testid={`theme-swatch-${t.id}`}
                  title={t.label}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Security - PIN Lock */}
        <div className="bg-card border border-border rounded-2xl p-5 md:p-6 mb-4" data-testid="settings-security-section">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">Security</h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">App Lock PIN</p>
                <p className="text-xs text-muted-foreground">Require PIN when returning to app</p>
              </div>
            </div>
            <button
              onClick={handleTogglePin}
              className={`relative w-12 h-7 rounded-full transition-colors ${settings.pin_enabled ? 'bg-primary' : 'bg-secondary'}`}
              data-testid="settings-pin-toggle"
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200 ${settings.pin_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {showPinSection && !settings.pin_enabled && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-4">
              <p className="text-xs text-muted-foreground mb-3">Enter a 4-digit PIN</p>
              <div className="flex justify-center space-x-3 mb-4">
                {[0, 1, 2, 3].map(i => (
                  <input
                    key={i}
                    id={`new-pin-${i}`}
                    type="password"
                    inputMode="numeric"
                    maxLength={1}
                    value={pinDigits[i]}
                    onChange={(e) => handlePinDigit(i, e.target.value, 'new')}
                    onKeyDown={(e) => handlePinKeyDown(i, e, 'new')}
                    className="w-12 h-14 text-center text-xl font-bold bg-input/30 border border-border rounded-xl focus:outline-none focus:border-primary"
                    data-testid={`pin-digit-${i}`}
                  />
                ))}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => { setShowPinSection(false); setPinDigits(['','','','']); }}
                  className="flex-1 text-sm text-muted-foreground hover:text-foreground py-2.5 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePin}
                  className="flex-1 bg-primary text-white rounded-xl py-2.5 text-sm font-medium hover:bg-primary/80 transition-colors"
                  data-testid="settings-save-pin"
                >
                  Enable PIN Lock
                </button>
              </div>
              {pinMsg && <p className="text-xs text-center text-primary mt-2">{pinMsg}</p>}
            </motion.div>
          )}

          {settings.pin_enabled && (
            <div className="mt-3 space-y-3">
              <div className="flex space-x-3">
                <button
                  onClick={() => { setPinAuthMode('old_pin'); setShowPinSection(true); setPinAuthenticated(false); }}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                  data-testid="settings-change-pin"
                >
                  Change PIN
                </button>
                <button
                  onClick={handleTogglePin}
                  className="text-xs text-red-400 hover:text-red-500 transition-colors"
                  data-testid="settings-disable-pin"
                >
                  Remove PIN
                </button>
              </div>

              {showPinSection && !pinAuthenticated && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-muted rounded-xl p-4">
                  <p className="text-xs text-muted-foreground mb-3 font-medium">Verify your identity to change PIN settings</p>
                  
                  {/* Primary: Login password */}
                  <input
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyEmail(); }}
                    placeholder="Enter your login password"
                    className="w-full bg-card border border-border rounded-xl px-3 py-2 text-sm mb-3 focus:outline-none focus:border-primary"
                    data-testid="pin-login-password-input"
                    autoFocus
                  />
                  <button onClick={handleVerifyEmail} className="w-full bg-primary text-white rounded-xl py-2 text-sm font-medium hover:bg-primary/80 mb-2">
                    Unlock with Login Password
                  </button>
                  
                  {/* Secondary: Old PIN toggle */}
                  {pinAuthMode !== 'old_pin' && (
                    <button
                      onClick={() => setPinAuthMode('old_pin')}
                      className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                    >
                      Or enter your current PIN instead
                    </button>
                  )}

                  {pinAuthMode === 'old_pin' && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2">Enter current 4-digit PIN</p>
                      <div className="flex justify-center space-x-3 mb-3">
                        {[0, 1, 2, 3].map(i => (
                          <input
                            key={i}
                            id={`old-pin-${i}`}
                            type="password"
                            inputMode="numeric"
                            maxLength={1}
                            value={oldPinDigits[i]}
                            onChange={(e) => handlePinDigit(i, e.target.value, 'old')}
                            onKeyDown={(e) => handlePinKeyDown(i, e, 'old')}
                            className="w-11 h-13 text-center text-lg font-bold bg-card border border-border rounded-xl focus:outline-none focus:border-primary"
                          />
                        ))}
                      </div>
                      <button onClick={handleVerifyOldPin} className="w-full bg-secondary text-foreground rounded-xl py-2 text-sm font-medium hover:bg-secondary/80">
                        Verify PIN
                      </button>
                    </div>
                  )}

                  {pinMsg && <p className="text-xs text-center text-red-400 mt-2">{pinMsg}</p>}
                </motion.div>
              )}

              {showPinSection && pinAuthenticated && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                  {pinAuthMode === 'old_pin' || pinAuthMode === 'email' ? (
                    <>
                      <p className="text-xs text-muted-foreground">Enter new 4-digit PIN, or remove PIN</p>
                      <div className="flex justify-center space-x-3 mb-3">
                        {[0, 1, 2, 3].map(i => (
                          <input
                            key={i}
                            id={`new-pin-${i}`}
                            type="password"
                            inputMode="numeric"
                            maxLength={1}
                            value={pinDigits[i]}
                            onChange={(e) => handlePinDigit(i, e.target.value, 'new')}
                            onKeyDown={(e) => handlePinKeyDown(i, e, 'new')}
                            className="w-12 h-14 text-center text-xl font-bold bg-card border border-border rounded-xl focus:outline-none focus:border-primary"
                          />
                        ))}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleDisablePin}
                          className="flex-1 text-sm text-red-400 border border-red-200 rounded-xl py-2 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          Remove PIN
                        </button>
                        <button
                          onClick={handleSavePin}
                          className="flex-1 bg-primary text-white rounded-xl py-2 text-sm font-medium hover:bg-primary/80 transition-colors"
                        >
                          Save New PIN
                        </button>
                      </div>
                    </>
                  ) : null}
                  {pinMsg && <p className="text-xs text-center text-primary mt-2">{pinMsg}</p>}
                </motion.div>
              )}
            </div>
          )}
        </div>

        {/* Secret Code */}
        <div className="bg-card border border-border rounded-2xl p-5 md:p-6 mb-4" data-testid="settings-secret-section">
          <div className="flex items-center space-x-3 mb-3">
            <Sparkles className="w-4 h-4 text-muted-foreground" />
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Secret Code</p>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={secretCode}
              onChange={(e) => setSecretCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSecretCode(); }}
              placeholder="Enter code..."
              className="flex-1 bg-card border border-border rounded-xl px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
              data-testid="secret-code-input"
            />
            <button
              onClick={handleSecretCode}
              disabled={!secretCode.trim()}
              className="bg-primary text-white rounded-xl px-4 py-2 text-sm font-medium hover:bg-primary/80 transition-colors disabled:opacity-40"
              data-testid="secret-code-submit"
            >
              Enter
            </button>
          </div>
          {secretMsg && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-primary mt-2 text-center" data-testid="secret-code-msg">{secretMsg}</motion.p>
          )}
        </div>

        {/* Quick Tour */}
        <div className="bg-card border border-border rounded-2xl p-5 md:p-6 mb-4" data-testid="settings-tour-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Quick Tour</h3>
                <p className="text-xs text-muted-foreground">Learn what NOYO can do</p>
              </div>
            </div>
            <button
              onClick={() => {
                // Set flag to trigger tour after navigation
                localStorage.setItem('noyo_trigger_tour', 'true');
                navigate('/');
              }}
              className="bg-primary/10 text-primary rounded-xl px-4 py-2 text-sm font-medium hover:bg-primary/20 transition-colors"
              data-testid="view-tour-button"
            >
              Start Tour
            </button>
          </div>
        </div>

        {/* Activity Section */}
        <div className="bg-card border border-border rounded-2xl p-5 md:p-6 mb-4" data-testid="settings-activity-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <Activity className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Activity Log</h3>
                <p className="text-xs text-muted-foreground">View all your recent activity</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/activity')}
              className="bg-secondary text-foreground rounded-xl px-4 py-2 text-sm font-medium hover:bg-secondary/80 transition-colors"
              data-testid="view-activity-button"
            >
              View
            </button>
          </div>
        </div>

        {/* Logout Section */}
        <div className="bg-card border border-border rounded-2xl p-5 md:p-6 mb-4" data-testid="settings-logout-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <LogOut className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Sign Out</h3>
                <p className="text-xs text-muted-foreground">Log out of your account</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="bg-secondary text-foreground rounded-xl px-4 py-2 text-sm font-medium hover:bg-secondary/80 transition-colors"
              data-testid="settings-logout-button"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Terms & Privacy */}
        <div className="bg-card border border-border rounded-2xl p-5 md:p-6 mt-4">
          <button
            onClick={() => navigate('/terms')}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-foreground">Terms & Privacy Policy</p>
                <p className="text-xs text-muted-foreground">How we handle your data</p>
              </div>
            </div>
            <ArrowLeft className="w-4 h-4 text-muted-foreground rotate-180" />
          </button>
        </div>

        {/* Danger Zone - Delete Account */}
        <div className="mt-10 border border-red-500/30 rounded-2xl p-5" data-testid="delete-account-section">
          <button
            onClick={() => setShowDeleteSection(!showDeleteSection)}
            className="w-full flex items-center justify-between"
            data-testid="delete-account-toggle"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div className="text-left">
                <h3 className="text-sm font-semibold text-red-500">Delete Account</h3>
                <p className="text-xs text-muted-foreground">Permanently delete your profile and all data</p>
              </div>
            </div>
            <ArrowLeft className={`w-4 h-4 text-red-400 transition-transform ${showDeleteSection ? 'rotate-[-90deg]' : 'rotate-180'}`} />
          </button>
          
          {showDeleteSection && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 space-y-3"
            >
              <div className="flex items-start space-x-2 bg-red-500/10 rounded-xl p-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-red-400 leading-relaxed">
                  <p className="font-semibold mb-1">This action is irreversible.</p>
                  <p>All your journal entries, stories, folders, audio recordings, and account data will be permanently deleted. This cannot be undone.</p>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  Type <span className="font-mono font-bold text-red-500">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="w-full bg-card border border-red-500/30 rounded-xl px-3 py-2 text-sm text-foreground focus:outline-none focus:border-red-500"
                  data-testid="delete-confirm-input"
                />
              </div>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || isDeletingAccount}
                className="w-full bg-red-600 text-white rounded-xl py-2.5 text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                data-testid="delete-account-button"
              >
                {isDeletingAccount ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Permanently Delete My Account</span>
                  </>
                )}
              </button>
              {deleteMsg && <p className="text-xs text-center text-red-500">{deleteMsg}</p>}
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default SettingsPage;
